import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../../utils/cn'

interface AssessmentPaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onPrevious?: () => void
  onNext?: () => void
  showSaveButton?: boolean
  onSave?: () => void
  className?: string
}

export function AssessmentPagination({
  currentPage,
  totalPages,
  onPageChange,
  onPrevious,
  onNext,
  showSaveButton = true,
  onSave,
  className,
}: AssessmentPaginationProps) {
  const canGoPrevious = currentPage > 1
  const canGoNext = currentPage < totalPages

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisible = 7

    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    // Always show first page
    pages.push(1)

    if (currentPage > 3) {
      pages.push('...')
    }

    // Show pages around current page
    for (
      let i = Math.max(2, currentPage - 1);
      i <= Math.min(totalPages - 1, currentPage + 1);
      i++
    ) {
      pages.push(i)
    }

    if (currentPage < totalPages - 2) {
      pages.push('...')
    }

    // Always show last page
    if (totalPages > 1) {
      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div
      className={cn(
        'flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4',
        className
      )}
    >
      {/* Previous Button */}
      <Button
        variant='outline'
        onClick={onPrevious}
        disabled={!canGoPrevious}
        className={cn(
          'min-w-[100px] rounded-lg border-gray-300 px-6 py-2 text-sm font-medium',
          !canGoPrevious && 'cursor-not-allowed opacity-40'
        )}
      >
        <ChevronLeft className='mr-1 h-4 w-4' />
        Previous
      </Button>

      {/* Page Numbers */}
      <div className='flex items-center gap-2'>
        {getPageNumbers().map((page, index) => {
          if (page === '...') {
            return (
              <span key={`ellipsis-${index}`} className='px-2 text-gray-400'>
                ...
              </span>
            )
          }

          const pageNumber = page as number
          const isActive = pageNumber === currentPage

          return (
            <button
              key={pageNumber}
              onClick={() => onPageChange(pageNumber)}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-lg text-sm font-medium transition-colors',
                isActive
                  ? 'bg-[#F97316] text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              )}
            >
              {pageNumber}
            </button>
          )
        })}
      </div>

      {/* Save & Continue Button */}
      {showSaveButton ? (
        <Button
          onClick={onSave}
          className='min-w-[160px] rounded-lg bg-gray-900 px-6 py-2 text-sm font-medium text-white hover:bg-gray-800'
        >
          Save & Continue
        </Button>
      ) : (
        <Button
          variant='outline'
          onClick={onNext}
          disabled={!canGoNext}
          className={cn(
            'min-w-[100px] rounded-lg border-gray-300 px-6 py-2 text-sm font-medium',
            !canGoNext && 'cursor-not-allowed opacity-40'
          )}
        >
          Next
          <ChevronRight className='ml-1 h-4 w-4' />
        </Button>
      )}
    </div>
  )
}
